import { clone } from "lodash-es";
import { useMemo } from "react";

export const DEFAULT_ORDER_FN = <T extends { order: number }>(a: T, b: T) => a.order - b.order;

export const orderByStringAttribute =
  <T extends { [key: string]: string }>(attribute: keyof T) =>
  (a: T, b: T) =>
    a[attribute].localeCompare(b[attribute], undefined, { sensitivity: "base" });

export const ORDER_BY_TITLE = <T extends { title: string }>(a: T, b: T) =>
  orderByStringAttribute<T>("title")(a, b);

export const ORDER_BY_CODE = <T extends { code: string }>(a: T, b: T) =>
  orderByStringAttribute<T>("code")(a, b);

export const ORDER_BY_NAME = <T extends { name: string }>(a: T, b: T) =>
  orderByStringAttribute<T>("name")(a, b);

export const ORDER_BY_CREATED = <T extends { created_at_ms: number; id: string }>(a: T, b: T) =>
  a.created_at_ms === b.created_at_ms
    ? a.id.localeCompare(b.id)
    : a.created_at_ms - b.created_at_ms;

export const ORDER_BY_CREATED_DATE_DESC = <T extends { created_at: string; id: string }>(
  a: T,
  b: T,
) =>
  a.created_at === b.created_at
    ? b.id.localeCompare(a.id)
    : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

export const useOrdered = <T extends { order: number }>(
  items: T[],
  orderFn?: (a: T, b: T) => number,
) => {
  return useCustomOrdered(items, orderFn || DEFAULT_ORDER_FN);
};

export const useCustomOrdered = <T>(items: T[], orderFn: (a: T, b: T) => number) => {
  return useMemo(() => {
    const cloned = items.map(clone);
    cloned.sort(orderFn);
    return cloned;
  }, [items, orderFn]);
};
